import React, { useState, useEffect } from "react";
import { 
  Bot, 
  Calendar, 
  User, 
  GraduationCap, 
  Languages, 
  Receipt, 
  Brain, 
  Phone, 
  Heart,
  Sparkles
} from "lucide-react";
import { SynapticText } from "@/components/hero/SynapticText";
import { NeuralNode } from "@/components/neural-network/NeuralNode";
import { NeuralConnection } from "@/components/neural-network/NeuralConnection";
import { NeuralBackground } from "@/components/neural-network/NeuralBackground";
import { useIsMobile } from "@/hooks/use-mobile.tsx";

const agents = [
  // Central Digital Human - Gia
  {
    icon: Brain,
    title: "Gia",
    description: "Lead Digital Human & Cognitive Screener - orchestrates and empowers all digital human® while conducting advanced cognitive assessments",
    clinicianBenefit: "Comprehensive cognitive screening and team coordination",
    patientBenefit: "Seamless, intelligent care coordination with advanced memory assessment",
    category: "Central Intelligence",
    color: "bg-primary",
    featured: true,
    central: true
  },
  // Supporting Digital Humans
  {
    icon: Bot,
    title: "Scribe",
    description: "Generates structured notes from clinical conversations",
    clinicianBenefit: "Clarity of content",
    patientBenefit: "Accurate medical records",
    category: "Documentation",
    color: "bg-blue-500",
    featured: true
  },
  {
    icon: Calendar,
    title: "Receptionist", 
    description: "Manages appointment scheduling and patient outreach",
    clinicianBenefit: "Automated scheduling",
    patientBenefit: "Responsiveness",
    category: "Operations",
    color: "bg-green-500"
  },
  {
    icon: User,
    title: "Nurse",
    description: "Coordinates patient care activities and handoffs",
    clinicianBenefit: "Seamless care coordination",
    patientBenefit: "Effective Listening",
    category: "Care Coordination",
    color: "bg-purple-500"
  },
  {
    icon: GraduationCap,
    title: "Medical Consultant",
    description: "Assists with procedures and clinical education",
    clinicianBenefit: "Expert guidance",
    patientBenefit: "Better comprehension of care plan",
    category: "Education",
    color: "bg-indigo-500",
    featured: true
  },
  {
    icon: Languages,
    title: "Medical Interpreter",
    description: "Instantly translates 20+ languages during patient conversations",
    clinicianBenefit: "Language barrier removal",
    patientBenefit: "Clear communication",
    category: "Communication",
    color: "bg-cyan-500"
  },
  {
    icon: Receipt,
    title: "Medical Coder",
    description: "Assigns correct billing codes based on documented encounters",
    clinicianBenefit: "Accurate billing",
    patientBenefit: "Proper insurance coverage",
    category: "Financial",
    color: "bg-amber-500"
  },
  {
    icon: Phone,
    title: "Contact-Center Agent",
    description: "Answers & triages > 70% of routine calls, reducing hold time by 45%",
    clinicianBenefit: "Reduced call volume",
    patientBenefit: "Faster response times",
    category: "Customer Service",
    color: "bg-teal-500"
  },
  {
    icon: Heart,
    title: "Wellness Agent",
    description: "Monitors staff wellbeing through private, automated check-ins",
    clinicianBenefit: "Staff wellness monitoring",
    patientBenefit: "Better care from healthy staff",
    category: "Wellness",
    color: "bg-pink-500"
  },
];

// Neural network positioning - Gia-centered hub layout with improved spacing
const getNeuralPositions = (isMobile: boolean) => {
  if (isMobile) {
    // Mobile layout - Gia at center with well-spaced agents orbiting around her
    return [
      { x: 50, y: 50, size: 'central' as const }, // Gia (absolute center)
      { x: 50, y: 15, size: 'large' as const }, // Scribe (top)
      { x: 15, y: 30, size: 'medium' as const }, // Receptionist (left)
      { x: 85, y: 30, size: 'medium' as const }, // Nurse (right)
      { x: 20, y: 75, size: 'large' as const }, // Medical Consultant (bottom-left)
      { x: 80, y: 75, size: 'medium' as const }, // Medical Interpreter (bottom-right)
      { x: 50, y: 85, size: 'medium' as const }, // Medical Coder (bottom)
      { x: 10, y: 55, size: 'small' as const }, // Contact-Center Agent (outer left)
      { x: 90, y: 55, size: 'small' as const }, // Wellness Agent (outer right)
    ];
  }
  
  // Desktop layout - Gia-centered with optimized spacing
  return [
    { x: 50, y: 50, size: 'central' as const }, // Gia (absolute center)
    
    // Inner circle - primary agents with better spacing
    { x: 50, y: 20, size: 'large' as const }, // Scribe (top)
    { x: 75, y: 30, size: 'medium' as const }, // Receptionist (top-right)
    { x: 85, y: 55, size: 'medium' as const }, // Nurse (right)
    { x: 70, y: 80, size: 'large' as const }, // Medical Consultant (bottom-right)
    
    // Outer circle - supporting agents with increased distance
    { x: 25, y: 30, size: 'medium' as const }, // Medical Interpreter (top-left)
    { x: 30, y: 80, size: 'medium' as const }, // Medical Coder (bottom-left)
    { x: 90, y: 20, size: 'small' as const }, // Contact-Center Agent (outer top-right)
    { x: 10, y: 80, size: 'small' as const }, // Wellness Agent (outer bottom-left)
  ];
};

// Neural connections - Gia as central hub with all agents
const neuralConnections = [
  // Primary hub connections - Gia to all major agents
  { from: 0, to: 1, thickness: 'thick' as const }, // Gia <-> Scribe
  { from: 0, to: 2, thickness: 'thick' as const }, // Gia <-> Receptionist  
  { from: 0, to: 3, thickness: 'thick' as const }, // Gia <-> Nurse
  { from: 0, to: 4, thickness: 'thick' as const }, // Gia <-> Medical Consultant
  
  // Secondary hub connections - Gia to supporting agents
  { from: 0, to: 5, thickness: 'medium' as const }, // Gia <-> Medical Interpreter
  { from: 0, to: 6, thickness: 'medium' as const }, // Gia <-> Medical Coder
  { from: 0, to: 7, thickness: 'medium' as const }, // Gia <-> Contact Center Agent
  { from: 0, to: 8, thickness: 'medium' as const }, // Gia <-> Wellness Agent
  
  // Inter-agent collaborative connections
  { from: 1, to: 4, thickness: 'thin' as const }, // Scribe <-> Medical Consultant
  { from: 2, to: 7, thickness: 'thin' as const }, // Receptionist <-> Contact Center
  { from: 3, to: 8, thickness: 'thin' as const }, // Nurse <-> Wellness Agent
  { from: 4, to: 5, thickness: 'thin' as const }, // Medical Consultant <-> Interpreter
  { from: 1, to: 6, thickness: 'thin' as const }, // Scribe <-> Medical Coder
];

export const DigitalTeam = () => {
  const [hoveredNode, setHoveredNode] = useState<number | null>(null);
  const [activeConnections, setActiveConnections] = useState<number[]>([]);
  const isMobile = useIsMobile();
  const neuralPositions = getNeuralPositions(isMobile);

  useEffect(() => {
    if (hoveredNode !== null) {
      const connections = neuralConnections
        .map((conn, index) => 
          (conn.from === hoveredNode || conn.to === hoveredNode) ? index : -1
        )
        .filter(index => index !== -1);
      setActiveConnections(connections);
    } else {
      setActiveConnections([]);
    }
  }, [hoveredNode]);

  return (
    <section className="py-24 relative overflow-hidden" style={{ background: 'var(--gradient-earth)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-400 px-3 py-2 sm:px-4 rounded-full mobile-text-xs sm:text-sm font-semibold mb-4 sm:mb-6">
            <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
            Digital Human® Technology
          </div>
          <h2 className="heading-display mobile-text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-4 sm:mb-6 px-4 sm:px-0 text-white">
            Meet Your{" "}
            <SynapticText 
              text="Digital Human® Team" 
              className="text-blue-400"
              delay={500}
            />
            <span className="block text-white/70 mt-2">
              <SynapticText 
                text="Built to Support You" 
                delay={1000}
                className="text-white/70"
              />
            </span>
          </h2>
          <p className="text-white/60 mobile-text-base sm:text-lg max-w-4xl mx-auto px-4 sm:px-0">
            <SynapticText 
              text="Experience the future of healthcare automation through our interconnected neural network of Digital Human® AI agents working in perfect harmony."
              delay={1500}
              className="text-white/60"
            />
          </p>
        </div>

        {/* Neural Network Visualization */}
        <div className={`relative mb-12 sm:mb-16 ${isMobile ? 'h-72 sm:h-80' : 'h-[600px]'}`}>
          
          {/* Neural Background */}
          <NeuralBackground />
          
          {/* Neural Connections SVG */}
          <svg 
            className="absolute inset-0 w-full h-full pointer-events-none z-10"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="neuralGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.8" />
                <stop offset="50%" stopColor="hsl(var(--accent))" stopOpacity="0.6" />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.4" />
              </linearGradient>
            </defs>
            
            {neuralConnections.map((connection, index) => (
              <NeuralConnection
                key={index}
                from={neuralPositions[connection.from]}
                to={neuralPositions[connection.to]}
                isActive={activeConnections.includes(index)}
                delay={index * 200}
                thickness={connection.thickness}
              />
            ))}
          </svg>
          
          {/* Neural Nodes */}
          {agents.map((agent, index) => (
            <NeuralNode
              key={index}
              agent={agent}
              position={neuralPositions[index]}
              index={index}
              onHover={setHoveredNode}
              isConnected={
                hoveredNode !== null && 
                neuralConnections.some(conn => 
                  (conn.from === hoveredNode && conn.to === index) ||
                  (conn.to === hoveredNode && conn.from === index)
                )
              }
              size={neuralPositions[index].size}
            />
          ))}

          {/* Mobile instruction overlay */}
          {isMobile && (
            <div className="absolute top-3 left-3 bg-background/90 backdrop-blur-sm rounded-lg px-3 py-2 mobile-text-xs text-muted-foreground border shadow-sm z-20">
              Tap nodes to explore
            </div>
          )}
        </div>

        {/* Network Status Display */}
        <div className="text-center animate-fade-in-up px-4 sm:px-0" style={{ animationDelay: '2s' }}>
          <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-400/80 px-4 py-2 sm:px-6 sm:py-3 rounded-full mobile-text-xs sm:text-sm">
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
            <SynapticText 
              text={`Neural Network Active • ${agents.length} Digital Human Connected`}
              delay={2500}
              className="text-blue-400/80"
            />
          </div>
          <p className="text-white/50 mobile-text-xs sm:text-sm mt-3 sm:mt-4 max-w-2xl mx-auto">
            <SynapticText 
               text={isMobile 
                ? "Tap any digital human® to see neural connections and detailed information" 
                : "Hover over any digital human® to see neural connections • Click for detailed digital human® information"}
              delay={3000}
              className="text-white/50"
            />
          </p>
        </div>
        
      </div>
    </section>
  );
};